import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { useAuth } from '../context/AuthContext';
import { 
  Wrench, 
  Search, 
  Star, 
  MapPin, 
  Phone, 
  Shield,
  ArrowLeft,
  Menu,
  X,
  Filter,
  SlidersHorizontal,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MechanicsListPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [bilingualOnly, setBilingualOnly] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [availableServices, setAvailableServices] = useState([]);

  useEffect(() => {
    fetchMechanics();
    fetchServices();
  }, []);

  const fetchMechanics = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (serviceFilter) params.append('service', serviceFilter);
      if (minRating > 0) params.append('min_rating', minRating.toString());
      if (bilingualOnly) params.append('bilingual', 'true');
      if (postalCode) params.append('postal_code', postalCode);
      params.append('sort_by', sortBy);
      
      const response = await axios.get(`${API_URL}/api/mechanics?${params.toString()}`);
      setMechanics(response.data);
    } catch (error) {
      console.error('Error fetching mechanics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mechanics/services/list`);
      setAvailableServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  // Re-fetch when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMechanics();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, serviceFilter, minRating, bilingualOnly, postalCode, sortBy]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setServiceFilter('');
    setMinRating(0);
    setBilingualOnly(false);
    setPostalCode('');
    setSortBy('rating');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/mechanics" className="text-orange-500 font-medium">Find Mechanics</Link>
              <Link to="/apply" className="text-slate-600 hover:text-slate-900 font-medium">For Mechanics</Link>
              {user ? (
                <>
                  <Button variant="ghost" onClick={() => navigate(user.role === 'admin' ? '/admin' : user.role === 'mechanic' ? '/mechanic' : '/dashboard')}>
                    Dashboard
                  </Button>
                  <Button variant="outline" onClick={handleLogout}>Logout</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate('/login')}>Login</Button>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => navigate('/register')}>Get Started</Button>
                </>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4">
            <div className="max-w-7xl mx-auto px-4 flex flex-col gap-4">
              <Link to="/mechanics" className="text-orange-500 py-2" onClick={() => setMobileMenuOpen(false)}>Find Mechanics</Link>
              <Link to="/apply" className="text-slate-600 py-2" onClick={() => setMobileMenuOpen(false)}>For Mechanics</Link>
              {user ? (
                <>
                  <Button variant="ghost" className="justify-start" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>Dashboard</Button>
                  <Button variant="outline" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>Logout</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" className="justify-start" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>Login</Button>
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>Get Started</Button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-4" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-['Barlow'] text-3xl font-bold text-slate-900" data-testid="page-title">
            Find Trusted Mechanics
          </h1>
          <p className="text-slate-600 mt-1">All mechanics are verified and committed to honest service</p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="flex gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, service, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-slate-300"
                data-testid="search-input"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="h-11"
              data-testid="filter-toggle"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {(serviceFilter || minRating > 0 || bilingualOnly || postalCode) && (
                <Badge className="ml-2 bg-orange-100 text-orange-700">Active</Badge>
              )}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <Card className="bg-white border border-slate-200" data-testid="filter-panel">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-5 gap-6">
                  {/* Your Location */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Your Postal Code</label>
                    <Input
                      placeholder="e.g., M5V"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value.toUpperCase())}
                      maxLength={7}
                      className="h-10"
                      data-testid="postal-code-input"
                    />
                    <p className="text-xs text-slate-500">Enter to find nearby mechanics</p>
                  </div>

                  {/* Service Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Service</label>
                    <Select value={serviceFilter || "all"} onValueChange={(v) => setServiceFilter(v === "all" ? "" : v)}>
                      <SelectTrigger data-testid="service-filter">
                        <SelectValue placeholder="All Services" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Services</SelectItem>
                        {availableServices.map((service) => (
                          <SelectItem key={service} value={service}>{service}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Min Rating */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Min Rating: {minRating > 0 ? `${minRating}+` : 'Any'}
                    </label>
                    <Slider
                      value={[minRating]}
                      onValueChange={(v) => setMinRating(v[0])}
                      max={5}
                      step={0.5}
                      className="py-2"
                      data-testid="rating-slider"
                    />
                  </div>

                  {/* Bilingual Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Language</label>
                    <Select value={bilingualOnly ? "bilingual" : "all"} onValueChange={(v) => setBilingualOnly(v === "bilingual")}>
                      <SelectTrigger data-testid="bilingual-filter">
                        <SelectValue placeholder="All Mechanics" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Mechanics</SelectItem>
                        <SelectItem value="bilingual">Bilingual Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sort By */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Sort By</label>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger data-testid="sort-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rating">Highest Rated</SelectItem>
                        <SelectItem value="reviews">Most Reviews</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters">
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Results Count */}
        <p className="text-sm text-slate-600 mb-4">
          {loading ? 'Loading...' : `${mechanics.length} mechanic${mechanics.length !== 1 ? 's' : ''} found`}
        </p>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : mechanics.length === 0 ? (
          <Card className="bg-white border border-slate-200">
            <CardContent className="py-12 text-center">
              <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No mechanics found
              </h3>
              <p className="text-slate-600 mb-4">
                Try adjusting your filters or search term
              </p>
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {mechanics.map((mechanic, index) => (
              <Card key={mechanic.mechanic_id} className={`bg-white border card-hover ${mechanic.serves_your_area === false ? 'border-slate-200 opacity-60' : 'border-slate-200'}`} data-testid={`mechanic-${mechanic.mechanic_id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h2 className="font-['Barlow'] text-xl font-bold text-slate-900">
                              Verified Mechanic #{index + 1}
                            </h2>
                            {mechanic.serves_your_area === true && (
                              <Badge className="bg-green-100 text-green-800 border border-green-200">
                                <MapPin className="w-3 h-3 mr-1" />
                                Serves Your Area
                              </Badge>
                            )}
                            {mechanic.serves_your_area === false && (
                              <Badge className="bg-red-100 text-red-800 border border-red-200">
                                Outside Service Area
                              </Badge>
                            )}
                            {mechanic.is_verified && (
                              <Badge className="bg-slate-100 text-slate-800 border border-slate-200">
                                <Shield className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            )}
                            {mechanic.is_bilingual && (
                              <Badge className="bg-blue-100 text-blue-800 border border-blue-200">
                                Bilingual
                              </Badge>
                            )}
                          </div>
                          {/* Rating & Reviews */}
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                              <span className="font-bold text-lg text-slate-900">{(mechanic.rating || 0).toFixed(1)}</span>
                              <span>({mechanic.total_reviews || 0} reviews)</span>
                            </div>
                            <div className="flex items-center gap-1 text-slate-500">
                              <MapPin className="w-4 h-4" />
                              <span>{mechanic.service_zone || 'Available in your area'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">{mechanic.years_experience || 5}+ years exp.</p>
                          {mechanic.is_bilingual && mechanic.languages?.length > 0 && (
                            <p className="text-sm text-blue-600">{mechanic.languages.join(', ')}</p>
                          )}
                        </div>
                      </div>

                      {/* Performance Metrics */}
                      <div className="grid grid-cols-4 gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                        <div className="text-center">
                          <p className="text-lg font-bold text-green-600">{mechanic.performance_metrics?.on_time_completion_rate || 100}%</p>
                          <p className="text-xs text-slate-500">On-Time</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-blue-600">{mechanic.performance_metrics?.response_time_hours || 0.5}h</p>
                          <p className="text-xs text-slate-500">Response</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-purple-600">{mechanic.performance_metrics?.jobs_completed || 0}</p>
                          <p className="text-xs text-slate-500">Jobs Done</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-orange-600">{100 - (mechanic.performance_metrics?.dispute_rate || 0)}%</p>
                          <p className="text-xs text-slate-500">Satisfaction</p>
                        </div>
                      </div>

                      {/* No Unnecessary Delays Badge */}
                      <div className="flex items-center gap-2 mb-3 p-2 bg-orange-50 rounded-lg border border-orange-200">
                        <Clock className="w-4 h-4 text-orange-600" />
                        <span className="text-sm text-orange-800 font-medium">No Unnecessary Delays</span>
                        <span className="text-xs text-orange-600">• Timely repairs with clear communication</span>
                      </div>

                      {/* Services */}
                      <div className="flex flex-wrap gap-2">
                        {mechanic.services?.slice(0, 5).map((service, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className={`bg-slate-50 border-slate-200 text-slate-700 ${
                              serviceFilter && service.toLowerCase().includes(serviceFilter.toLowerCase()) 
                                ? 'bg-orange-50 border-orange-200 text-orange-700' 
                                : ''
                            }`}
                          >
                            {service}
                          </Badge>
                        ))}
                        {(mechanic.services?.length || 0) > 5 && (
                          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-500">
                            +{mechanic.services.length - 5} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 md:w-40">
                      <Button 
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={() => navigate(`/mechanics/${mechanic.mechanic_id}`)}
                        data-testid={`view-mechanic-${mechanic.mechanic_id}`}
                      >
                        View Details
                      </Button>
                      {user && user.role === 'customer' && (
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/book/${mechanic.mechanic_id}`)}
                          data-testid={`book-mechanic-${mechanic.mechanic_id}`}
                        >
                          Book Now
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MechanicsListPage;
